import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller } from "../../../CommonElements/Controller";
import { useForm } from "react-hook-form";
import { Row, Form } from "reactstrap";
import { AntInput } from "../../../CommonElements/AntInput";
import { Flex, Divider, Skeleton } from "antd";
import { Button } from "react-bootstrap";
import { Spinner } from "react-bootstrap";
import { cn } from "../../../utils/helpers";
import { FileUpload } from "../../../CommonElements/FileUpload";
import { ImageUpload01Icon } from "@hugeicons/core-free-icons";
import { Link } from "react-router";
import { useValidation } from "@/utils/hooks/useValidation";
import { ActionsFooter } from "@/Component/ActionsFooter";

export const UpdateHousingContractForm = ({
  defaultValues = {},
  onSubmit: handleFormSubmit,
  isPending = false,
}) => {
  const { t } = useTranslation();
  const { maxLengthValidation, numbersOnlyValidation } = useValidation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
  });

  // Add state for image preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const sketchImage = watch("SketchImageUrl");

  // Update preview when image changes
  useEffect(() => {
    let currentPreviewUrl = null;

    if (sketchImage) {
      const file = sketchImage?.originFileObj || sketchImage;
      if (file instanceof File) {
        currentPreviewUrl = URL.createObjectURL(file);
        setPreviewUrl((prevUrl) => {
          // Clean up previous URL
          if (prevUrl) {
            URL.revokeObjectURL(prevUrl);
          }
          return currentPreviewUrl;
        });
      } else if (typeof sketchImage === "string") {
        // Handle existing image URL from API
        setImageLoading(true); // Set loading when switching to server image
        setPreviewUrl(sketchImage);
      } else {
        setPreviewUrl((prevUrl) => {
          if (prevUrl) {
            URL.revokeObjectURL(prevUrl);
          }
          return null;
        });
      }
    } else {
      setPreviewUrl((prevUrl) => {
        if (prevUrl) {
          URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    }

    // Cleanup function
    return () => {
      if (currentPreviewUrl) {
        URL.revokeObjectURL(currentPreviewUrl);
      }
    };
  }, [sketchImage]);

  const onSubmit = (data) => {
    // Extract the file from the form data
    const file = data.SketchImageName?.originFileObj || data.SketchImageName;
    const payload = {
      ...data,
      SketchImageName: file instanceof File ? file : null,
    };
    handleFormSubmit(payload);
  };

  const maxLength = 4;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row className="w-75">
        <CONTROLLER
          labelProps={{
            htmlFor: "BuildingNo",
            children: t("BUILDING_NO"),
          }}
          controllerProps={{
            errors,
            name: "BuildingNo",
            control: control,
            rules: {
              ...maxLengthValidation(maxLength),
              ...numbersOnlyValidation(),
            },
            render: ({ field }) => (
              <AntInput
                {...field}
                maxLength={maxLength}
                id="BuildingNo"
                name="BuildingNo"
                placeholder={t("BUILDING_NO_PLACEHOLDER")}
              />
            ),
          }}
        />
        <CONTROLLER
          labelProps={{
            htmlFor: "ADDRESS",
            children: t("ADDRESS"),
          }}
          controllerProps={{
            errors,
            name: "HOUSE_ADDRESS_1",
            control: control,
            rules: {
              maxLength: {
                value: 255,
                message: t("MAX_LENGTH", { max: 255 }),
              }
            },
            render: ({ field }) => (
              <AntInput
                showCount
                maxLength={255}
                {...field}
                id="ADDRESS"
                placeholder={t("ADDRESS")}
              />
            ),
          }}
        />
        <CONTROLLER
          labelProps={{
            htmlFor: "Location",
            children: t("HOUSING_LOCATION_URL"),
          }}
          controllerProps={{
            errors,
            name: "Location",
            control: control,
            rules: {
              maxLength: {
                value: 2000,
                message: t("MAX_LENGTH", { max: 2000 }),
              },
              validate: (value) => {
                const v = value != null ? String(value).trim() : "";
                if (!v) return true;
                try {
                  const u = new URL(v);
                  if (u.protocol !== "http:" && u.protocol !== "https:") {
                    return t("HOUSING_LOCATION_URL_INVALID");
                  }
                  return true;
                } catch {
                  return t("HOUSING_LOCATION_URL_INVALID");
                }
              },
            },
            render: ({ field }) => (
              <AntInput
                {...field}
                type="url"
                inputMode="url"
                autoComplete="off"
                id="Location"
                name="Location"
                showCount
                maxLength={2000}
                placeholder={t("HOUSING_LOCATION_URL_PLACEHOLDER")}
                value={field.value ?? ""}
              />
            ),
          }}
        />
        <CONTROLLER
          labelProps={{
            htmlFor: "BuildingName",
            children: t("BUILDING_NAME"),
          }}
          controllerProps={{
            errors,
            name: "BuildingName",
            control: control,
            render: ({ field }) => (
              <AntInput
                {...field}
                id="BuildingName"
                name="BuildingName"
                placeholder={t("BUILDING_NAME_PLACEHOLDER")}
              />
            ),
          }}
        />

        {/* Area Number - رقم المنطقة */}
        <CONTROLLER
          labelProps={{
            htmlFor: "AreaNo",
            children: t("AREA_NO"),
          }}
          rules={{
            ...maxLengthValidation(maxLength),
            ...numbersOnlyValidation(),
          }}
          controllerProps={{
            errors,
            name: "AreaNo",
            control: control,
            render: ({ field }) => (
              <AntInput
                {...field}
                id="AreaNo"
                name="AreaNo"
                placeholder={t("AREA_NO_PLACEHOLDER")}
                maxLength={maxLength}
              />
            ),
          }}
        />

        {/* Delegate Name - اسم المفوض */}
        <CONTROLLER
          labelProps={{
            htmlFor: "OwnerName",
            children: t("DELEGATE_NAME"),
          }}
          controllerProps={{
            errors,
            name: "OwnerName",
            control: control,
            render: ({ field }) => (
              <AntInput
                {...field}
                id="OwnerName"
                name="OwnerName"
                placeholder={t("DELEGATE_NAME_PLACEHOLDER")}
                status={errors.OwnerName ? "error" : undefined}
              />
            ),
          }}
        />

        {/* Longitude - خط الطول */}
        <CONTROLLER
          labelProps={{
            htmlFor: "Longitude",
            children: t("LONGITUDE"),
          }}
          controllerProps={{
            errors,
            name: "Longitude",
            control: control,
            rules: {
              validate: {
                isNumber: (value) => {
                  if (!value) return true;
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return t("LONGITUDE_MUST_BE_NUMBER");
                  }
                  return true;
                },
                validRange: (value) => {
                  if (!value) return true;
                  const num = parseFloat(value);
                  if (num < -180 || num > 180) {
                    return t("LONGITUDE_INVALID_RANGE");
                  }
                  return true;
                },
              },
            },
            render: ({ field }) => (
              <AntInput
                {...field}
                id="Longitude"
                name="Longitude"
                status={errors.Longitude ? "error" : undefined}
                placeholder={t("LONGITUDE_PLACEHOLDER")}
              />
            ),
          }}
        />

        {/* Latitude - خط العرض */}
        <CONTROLLER
          labelProps={{
            htmlFor: "Latitude",
            children: t("LATITUDE"),
          }}
          controllerProps={{
            errors,
            name: "Latitude",
            control: control,
            rules: {
              validate: {
                isNumber: (value) => {
                  if (!value) return true;
                  const num = parseFloat(value);
                  if (isNaN(num)) {
                    return t("LATITUDE_MUST_BE_NUMBER");
                  }
                  return true;
                },
                validRange: (value) => {
                  if (!value) return true;
                  const num = parseFloat(value);
                  if (num < -90 || num > 90) {
                    return t("LATITUDE_INVALID_RANGE");
                  }
                  return true;
                },
              },
            },
            render: ({ field }) => (
              <AntInput
                {...field}
                id="Latitude"
                name="Latitude"
                placeholder={t("LATITUDE_PLACEHOLDER")}
                status={errors.Latitude ? "error" : undefined}
              />
            ),
          }}
        />

        {/* Mobile Number - رقم الجوال */}
        <CONTROLLER
          labelProps={{
            htmlFor: "REP_MOBILE_NO",
            children: t("DELEGATE_MOBILE_NUMBER"),
          }}
          controllerProps={{
            errors,
            name: "REP_MOBILE_NO",
            control: control,
            rules: {},
            render: ({ field }) => (
              <AntInput
                {...field}
                id="REP_MOBILE_NO"
                name="REP_MOBILE_NO"
                placeholder={t("MOBILE_NUMBER")}
                status={errors.REP_MOBILE_NO ? "error" : undefined}
                inputMode="numeric"
                maxLength={10}
              />
            ),
          }}
        />
        {/* Image Upload - إرفق صورة */}
        <CONTROLLER
          labelProps={{
            htmlFor: "SketchImageName",
            children: t("UPLOAD_IMAGE"),
          }}
          controllerProps={{
            errors,
            name: "SketchImageName",
            control: control,
            render: ({ field }) => (
              <div>
                <FileUpload
                  onFileSelect={(file) => {
                    field.onChange(file?.originFileObj || file || null);
                  }}
                  accept="image/*"
                  maxSizeInMB={5}
                  maxCount={1}
                  buttonText="UPLOAD_IMAGE"
                  icon={ImageUpload01Icon}
                />
                {previewUrl && (
                  <div style={{ marginTop: "12px", position: "relative" }}>
                    {imageLoading && !previewUrl.includes("http") && (
                      <Skeleton.Image
                        active
                        style={{
                          width: "150px",
                          height: "150px",
                          borderRadius: "8px",
                        }}
                      />
                    )}
                    <img
                      src={
                        previewUrl.includes("http")
                          ? previewUrl
                          : `${process.env.REACT_APP_API_URL.replace(
                            "/api/v1/",
                            ""
                          )}/uploads/house-sketches/${defaultValues.SketchImageName
                          }`
                      }
                      alt="Preview"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        borderRadius: "8px",
                        border: "1px solid #ddd",
                        opacity:
                          imageLoading && !previewUrl.includes("http")
                            ? 0.5
                            : 1,
                      }}
                      onLoad={() => setImageLoading(false)}
                      onError={() => setImageLoading(false)}
                    />
                  </div>
                )}
              </div>
            ),
          }}
        />
      </Row>

      <ActionsFooter>
        <Button variant="primary" type="submit" disabled={isPending}>
          {isPending && <Spinner size="sm" />}
          {t("SAVE_CHANGES")}
        </Button>
        <Link
          to={-1}
          className="btn btn-danger"
          type="button"
          disabled={isPending}
        >
          {t("CANCEL")}
        </Link>
      </ActionsFooter>
    </Form>
  );
};

const CONTROLLER = (props) => {
  const label = props.labelProps || {};
  return (
    <Controller
      {...props}
      labelProps={{
        ...label,
        className: cn("fw-bold", label?.className),
      }}
      layout={{
        colProps: { xs: 12, md: 6 },
        stackProps: { gap: 1 },
      }}
    />
  );
};
